import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "reactstrap";
import { fetchCourseListForWebThunk } from "slices/CourseWeb/thunk";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import type { RootState } from "store";
import DashboardHeader from "../../Components/Common/DashboardHeader";

const selectCourseWebState = (state: RootState) => (state as any).courseWeb;

const courseDataSelector = createSelector(selectCourseWebState, (courseWeb) => ({
  courseData: courseWeb.courses,
  loading: courseWeb.loading,
  pagination: courseWeb.pagination,
}));

const selectLoginState = (state: any) => state.Login;
const loginSelector = createSelector(selectLoginState, (login: any) => ({
  isAuthenticated: login.isAuthenticated,
}));

const ExploreCourses = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector(loginSelector);
  const { courseData, loading, pagination } = useSelector(courseDataSelector);
  const [currentPage, setCurrentPage] = useState(1);
  const coursesPerPage = 2; // Show 2 courses per page as per design

  useEffect(() => {
    dispatch(
      fetchCourseListForWebThunk({
        page: currentPage,
        per_page: coursesPerPage,
        from_date: "",
        to_date: "",
        course_id: 0,
        searchkey: "",
      })
    );
  }, [dispatch, currentPage]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = pagination?.total_pages || 1;
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleViewNow = async (courseId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      try {
        await dispatch(
          fetchCourseListForWebThunk({
            page: 1,
            per_page: 10,
            from_date: "",
            to_date: "",
            course_id: courseId,
            searchkey: "",
          })
        );
        navigate(`/services/${courseId}`);
      } catch (error) {
        console.error("Error fetching course details:", error);
        navigate(`/services/${courseId}`);
      }
    } else {
      navigate(`/course/${courseId}`);
    }
  };

  const totalPages = pagination?.total_pages || 1;
  const currentPageIndex = currentPage - 1;

  document.title = "Explore Courses - Amanat";

  return (
    <div className="explore-courses-page">
      <DashboardHeader />

      <main className="explore-main">
        <div className="container">
          <section className="intro-section">
            <h1 className="main-heading">Explore Courses</h1>
            <p className="intro-text">
              Amanat helps you learn the latest, in-demand skills quickly and easily, from anywhere. 
              Whether you're starting your career, switching fields, or simply leveling up, Amanat gives 
              you the right courses to stay ahead in today's fast-changing job market. With expert instructors, 
              practical lessons, and learning at your own pace, Amanat makes growing in your career simpler than ever.
            </p>
          </section>

          <section className="courses-section">
            {loading ? (
              <div className="loading-state">Loading courses...</div>
            ) : courseData && courseData.length > 0 ? (
              <div className="course-cards">
                {courseData.map((course: any) => (
                  <Link 
                    key={course.id} 
                    to={isAuthenticated ? `/course/${course.id}` : `/services/${course.id}`}
                    className="course-card-link"
                  >
                    <div className="course-card">
                      <div className="course-image">
                        <img 
                          src={course.thumbnail_url || "https://via.placeholder.com/400x250"} 
                          alt={course.course_name}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "https://via.placeholder.com/400x250";
                          }}
                        />
                      </div>
                      <div className="course-content">
                        <h2 className="course-title">{course.course_name || "Course Title"}</h2>
                        <p className="course-description">
                          {course.description || "Learn fundamental and advanced data analysis skills, covering Python, SQL, statistics, data visualization, and business intelligence tools."}
                        </p>
                        <Button 
                          className="view-now-btn"
                          onClick={(e) => handleViewNow(course.id, e)}
                        >
                          View Now
                          <i className="ri-arrow-right-line"></i>
                        </Button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="no-courses">No courses available at the moment.</div>
            )}

            {/* Pagination Controls */}
            {!loading && courseData && courseData.length > 0 && (
              <div className="pagination-controls">
                <button 
                  className="pagination-btn" 
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <div className="pagination-dots">
                  {Array.from({ length: totalPages }, (_, index) => (
                    <span
                      key={index}
                      className={`dot ${index === currentPageIndex ? "active" : ""}`}
                      onClick={() => setCurrentPage(index + 1)}
                    ></span>
                  ))}
                </div>
                <button 
                  className="pagination-btn" 
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                  aria-label="Next page"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default ExploreCourses;

